/** The management API, as the inspector sees it. */
import { API_PREFIX } from './prefix.js';

export interface DefaultResponse {
  status: number;
  headers: Record<string, string>;
  body?: unknown;
}

export interface EndpointInfo {
  slug: string;
  url: string;
  name: string | null;
  defaultResponse: DefaultResponse;
  requestCount: number;
  createdAt: string;
  expiresAt: string;
}

/** An endpoint plus its token — only ever returned by the create call. */
export interface CreatedEndpoint extends EndpointInfo {
  token: string;
}

export type BodyEncoding = 'utf8' | 'base64';

export interface CapturedRequest {
  id: string;
  /** Arrival order. Also the SSE event id, which is how resuming stays exact. */
  seq: number;
  method: string;
  path: string;
  query: Record<string, unknown>;
  headers: Record<string, string | string[]>;
  body: string | null;
  bodyEncoding: BodyEncoding | null;
  /** Bytes actually received, which is more than `body` when truncated. */
  bodySize: number;
  bodyTruncated: boolean;
  contentType: string | null;
  ip: string | null;
  userAgent: string | null;
  statusReturned: number;
  durationMs: number;
  ruleId: string | null;
  createdAt: string;
}

export interface RequestPage {
  endpoint: EndpointInfo;
  requests: CapturedRequest[];
  nextCursor: string | null;
  total: number;
}

/**
 * Carries the API's `error.code`, so callers branch on a stable string instead
 * of matching prose or guessing from the status.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }

  /** True when the token will not start working on its own. */
  get isAuthFailure(): boolean {
    return this.code === 'invalid_token' || this.code === 'missing_token';
  }

  /** True when the endpoint is not reachable anymore — never existed, or expired. */
  get isGone(): boolean {
    return this.code === 'endpoint_not_found' || this.code === 'endpoint_expired';
  }
}

async function request<T>(url: string, init: RequestInit = {}): Promise<T> {
  let response: Response;

  try {
    response = await fetch(url, init);
  } catch (error) {
    // A dead network is not an HTTP status; give it a code of its own so the
    // UI can say "sin conexión" instead of inventing a server error.
    throw new ApiError(0, 'network_error', error instanceof Error ? error.message : 'network');
  }

  const payload: unknown = await response.json().catch(() => undefined);

  if (!response.ok) {
    const apiError = (payload as { error?: { code?: string; message?: string } } | undefined)
      ?.error;
    throw new ApiError(
      response.status,
      apiError?.code ?? 'internal_error',
      apiError?.message ?? `HTTP ${response.status}`,
    );
  }

  return payload as T;
}

function auth(token: string): Record<string, string> {
  return { authorization: `Bearer ${token}` };
}

export async function createEndpoint(body?: {
  name?: string;
  ttlDays?: number;
}): Promise<CreatedEndpoint> {
  return request<CreatedEndpoint>(`${API_PREFIX}/endpoints`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body ?? {}),
  });
}

export async function listRequests(
  slug: string,
  token: string,
  options: { limit?: number; cursor?: string } = {},
): Promise<RequestPage> {
  const query = new URLSearchParams();
  if (options.limit !== undefined) query.set('limit', String(options.limit));
  if (options.cursor) query.set('cursor', options.cursor);

  const suffix = query.size > 0 ? `?${query}` : '';
  return request<RequestPage>(`${API_PREFIX}/endpoints/${slug}/requests${suffix}`, {
    headers: auth(token),
  });
}

export async function clearRequests(slug: string, token: string): Promise<number> {
  const result = await request<{ deleted: number }>(`${API_PREFIX}/endpoints/${slug}/requests`, {
    method: 'DELETE',
    headers: auth(token),
  });
  return result.deleted;
}

/**
 * Pushes the expiry out by `ttlDays` (defaults server-side to the TTL) and
 * returns the endpoint with its new `expiresAt`. The one management call that
 * keeps working on an expired endpoint, while the cleanup job has not deleted
 * it yet.
 */
export async function extendEndpoint(
  slug: string,
  token: string,
  body?: { ttlDays?: number },
): Promise<EndpointInfo> {
  // Only claim JSON when something is actually sent: a `content-type` with no
  // body behind it is what servers reject as an empty JSON payload.
  return request<EndpointInfo>(`${API_PREFIX}/endpoints/${slug}/extend`, {
    method: 'POST',
    headers: body ? { ...auth(token), 'content-type': 'application/json' } : auth(token),
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
}
